// Theme manager — light/dark for the whole web app (Editorial stays the one
// design language; dark is a token-level re-skin, not a second theme).
//
// Model: `jg.theme` in localStorage holds an explicit 'light' | 'dark'
// choice; absent = follow the OS (prefers-color-scheme), live. The resolved
// theme is stamped as `data-theme` on <html>, which the CSS dark blocks
// (globals.css / tokens.css) key off — call sites read tokens and never
// branch on theme. index.html runs a tiny pre-paint copy of this resolution
// so first paint is already correct; this module takes over after hydrate.
//
// Maps note: CSS variables flip GL *chrome* (HTML markers, cards) for free,
// but mapbox paint properties can't read CSS vars — VenueMap/TransitMap
// subscribe here and re-paint (see lib/editorial-basemap.ts).

import { useSyncExternalStore } from 'react';

export type ThemePref = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORE_KEY = 'jg.theme';
// Matches the browser-chrome tint to the app bg (index.html ships the light one).
const META_COLOR: Record<ResolvedTheme, string> = { light: '#f4efe6', dark: '#16130e' };

const listeners = new Set<() => void>();

function systemDark(): boolean {
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches;
}

export function getThemePref(): ThemePref {
  try {
    const v = localStorage.getItem(STORE_KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

export function resolvedTheme(): ResolvedTheme {
  const pref = getThemePref();
  if (pref === 'system') return systemDark() ? 'dark' : 'light';
  return pref;
}

function apply(): void {
  const t = resolvedTheme();
  const root = document.documentElement;
  if (root.getAttribute('data-theme') !== t) root.setAttribute('data-theme', t);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', META_COLOR[t]);
  for (const cb of listeners) cb();
}

export function setThemePref(pref: ThemePref): void {
  try {
    if (pref === 'system') localStorage.removeItem(STORE_KEY);
    else localStorage.setItem(STORE_KEY, pref);
  } catch {
    // private mode — the in-session attribute flip below still works
  }
  apply();
}

/** The toggle affordance: flip to the opposite of whatever is showing. */
export function toggleTheme(): void {
  setThemePref(resolvedTheme() === 'dark' ? 'light' : 'dark');
}

export function subscribeTheme(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Resolved theme as reactive state — re-renders on toggle / OS change. */
export function useTheme(): ResolvedTheme {
  return useSyncExternalStore(subscribeTheme, resolvedTheme, () => 'light');
}

/** The stored preference (three-way, incl. 'system') as reactive state. */
export function useThemePref(): ThemePref {
  return useSyncExternalStore(subscribeTheme, getThemePref, () => 'system');
}

/** Boot: stamp the attribute (idempotent with the pre-paint script) and
 *  track OS changes while the pref is 'system'. Called once from main.tsx. */
export function initTheme(): void {
  apply();
  matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (getThemePref() === 'system') apply();
  });
  // A second tab changing the pref updates this one too.
  window.addEventListener('storage', (e) => {
    if (e.key === STORE_KEY) apply();
  });
}
